﻿import React, { useRef, useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchRecordsData,
  selectAllRecords,
  selectLastAnalyticsRunUtc,
  selectRecordsLoadedStatus,
  selectLifecyclePopupOpen,
  setLifecyclePopupOpen,
  setLifecycleDetails,
  setWorkflowStepsInfo,
  setApplyFilter,
  setSelectedRecord,
} from "@/features/homepage/workflowManagerSlice";
import {
  setFilterType,
  setFilterStatus,
  selectFilterType,
  selectFilterStatus,
  selectFilters,
  selectWorkflow,
  setSessionPagination,
  selectSessionPagination,
  statusTypes,
  setManagerSearchString,
  selectManagerSearchString,
} from "@/features/homepage/workflowManagerSessionSlice";
import {
  selectConnection,
  selectLifecycle,
  selectAllWorkflows,
} from "@/features/homepage/homeSlice";
import { selectHomeDateFilter } from "@/features/dateFilterSlice";
import {
  Typography,
  Box,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import "./WorkflowManagerPage.css";
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  useGridApiRef,
} from "@mui/x-data-grid";
import {
  ShepCheckbox,
  ShepPagination,
  ShepTooltip,
  ShepDataGridPro,
} from "@/components/GenericComponents/ShepComponents";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ThreeDotSettingMenu from "./ThreeDotSettingMenu/ThreeDotSettingMenu";
import {
  ArchiveIcon,
  LifecycleIcon,
  SearchIcon,
  SplitIcon,
  HoldIcon,
  ConsolidateIcon,
} from "@/ImageRepo";
import {
  setIsMapOnFlyOpen,
  selectIsMapOnFlyOpen,
} from "@/features/connections/crossReferenceSlice";

import { useTranslation } from "react-i18next";
import MapOnFly from "@/components/Maintenance/MapOnFly/MapOnFly";
import HorizontalWorkflowMenu from "./HorizontalWorkflowMenu";
import LifeCyclePopup from "../LifecyclePopup/LifecyclePopup";
import axios from "@/features/axiosHelper";
import {
  FilterType,
  FilterDropDownType,
  StatusFilterType,
} from "@/utils/utils";
import _ from "lodash";
import CustomFilterDropdown from "@/components/CustomFilterDropdown/CustomFilterDropdown";

const getStatusClass = (status) => {
  switch (status) {
    case statusTypes.Successful:
      return "wf-manager-status-success";
    case statusTypes.CompletedWithWarning:
      return "wf-manager-status-completedwithwarning";
    case statusTypes.Error:
      return "wf-manager-status-error";
    case statusTypes.Processing:
    case statusTypes.PendingAck:
      return "wf-manager-status-processing";
    case statusTypes.NotQueued:
      return "wf-manager-status-notqueued";
    default:
      return "wf-manager-status-processing";
  }
};

const SetStatus = (status, t) => {
  //return `${status} 1234`;
  switch (status) {
    case statusTypes.Successful:
      return t("workflow-manager.statusCompletedLabel");
    case statusTypes.CompletedWithWarning:
      return t(
        "workflow-manager.statusCompletedWithWarningLabel",
        "Completed With Warning",
      );
    case statusTypes.Error:
      return t("workflow-manager.statusErrorLabel");
    case statusTypes.Processing:
      return t("workflow-manager.statusProcessingLabel");
    case statusTypes.PendingAck:
      return t("workflow-manager.statusPendingAckLabel");
    case statusTypes.NotQueued:
      return t("workflow-manager.statusNotQueuedLabel");
    default:
      return t("workflow-manager.statusProcessingLabel");
  }
};

const StatusCell = ({ row, onStatusClick }) => {
  const statusClass = getStatusClass(row.status);
  const handleClick = (event) => {
    event.stopPropagation();
    onStatusClick(row);
  };
  const { t } = useTranslation("homepage");
  return (
    <ShepTooltip title={SetStatus(row.status, t) ?? statusTypes.Processing}>
      <div
        onClick={handleClick}
        className={statusClass}
        style={{ cursor: "pointer" }}
      ></div>
    </ShepTooltip>
  );
};

const getRelativeRefreshLabel = (lastAnalyticsRunAt, now) => {
  if (!lastAnalyticsRunAt) {
    return "Last updated: pending";
  }

  const elapsedSeconds = Math.max(now.diff(lastAnalyticsRunAt, "second"), 0);
  if (elapsedSeconds < 10) {
    return "Last updated just now";
  }
  if (elapsedSeconds < 60) {
    return `Last updated ${elapsedSeconds} seconds ago`;
  }

  const elapsedMinutes = now.diff(lastAnalyticsRunAt, "minute");
  if (elapsedMinutes < 60) {
    return `Last updated ${elapsedMinutes} minute${elapsedMinutes === 1 ? "" : "s"} ago`;
  }

  const elapsedHours = now.diff(lastAnalyticsRunAt, "hour");
  return `Last updated ${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`;
};

const WorkflowManagerPage = () => {
  dayjs.extend(utc);
  const { t } = useTranslation("homepage");
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const records = useSelector(selectAllRecords);
  const lastAnalyticsRunUtc = useSelector(selectLastAnalyticsRunUtc);
  const { startDate, endDate } = useSelector(selectHomeDateFilter) || {};
  const currentOperationId = useSelector(selectWorkflow);
  const currentConnectionId = useSelector(selectConnection);
  const workflows = useSelector(selectAllWorkflows);
  const [workflowName, setWorkflowName] = useState("false");
  const [lastAnalyticsRunAt, setLastAnalyticsRunAt] = useState(null);
  const [refreshedDisplayTime, setRefreshedDisplayTime] = useState(dayjs());
  const filters = useSelector(selectFilters);
  const searchString = useSelector(selectManagerSearchString);
  const [selectionModel, setSelectionModel] = useState([]);

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  useEffect(() => {
    //SHEP-727: Sets workflow table name to "" after a page refresh (when workflows is blank and
    //currentOperationId hasn't changed) while waiting for backend to fetch workflows values
    if (!workflows || workflows.length === 0) {
      setWorkflowName("");
    } else {
      let wf = workflows.find((w) => w.OperationId === currentOperationId);
      if (!wf) setWorkflowName("Work in progress workflow");
      else {
        setWorkflowName(t(wf.NameMessageId, wf.OperationName));
      }
    }
  }, [currentOperationId, workflows]);

  const getRecordsData = (clearCache = false) => {
    let propStartDate = startDate;
    let propEndDate = endDate;
    const defaultStartDays = 7;

    if (propStartDate === "" && propEndDate === "") {
      propStartDate = dayjs().subtract(defaultStartDays, "day").startOf("day");
      propEndDate = dayjs();
    } else if (propEndDate === null) {
      propEndDate = propStartDate;
    }
    if (!propStartDate || !propEndDate) return; // Block until date filter ready
    propEndDate = propEndDate.add(1, "day"); // always set to end of day
    dispatch(
      fetchRecordsData({
        operationId: currentOperationId,
        startDate: propStartDate,
        endDate: propEndDate,
        connectionId:
          currentConnectionId === "All" ? -1 : currentConnectionId.ConnectionId,
        clearCache: clearCache,
      }),
    );
  };

  useEffect(() => {
    setLastAnalyticsRunAt(
      lastAnalyticsRunUtc ? dayjs.utc(lastAnalyticsRunUtc) : null,
    );
  }, [lastAnalyticsRunUtc]);

  useEffect(() => {
    getRecordsData();
  }, [startDate, endDate, currentConnectionId]);
  const triggerSearchRecords = () => {
    getRecordsData();
  };

  const previousOperationId = usePrevious(currentOperationId);
  useEffect(() => {
    //useprevious allows for us to only call this when currentOperationId has changed, not on mount
    if (previousOperationId !== currentOperationId) {
      const isWfSwitch =
        previousOperationId !== undefined &&
        previousOperationId !== null &&
        previousOperationId !== "";
      if (isWfSwitch && searchString !== "") {
        dispatch(setManagerSearchString({ searchString: "" }));
      }
      getRecordsData();
    }
  }, [currentOperationId]);

  useEffect(() => {
    if (filters.applyFilters) {
      getRecordsData();
    }
  }, [filters]); // SHEP-141: Refresh grid data when filters change

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshedDisplayTime(dayjs());
    }, 10000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    return () => {
      dispatch(setManagerSearchString({ searchString: "" }));
      dispatch(setApplyFilter({ applyFilter: false }));
    };
  }, []); //clears searchstring and filters on unmount

  const transformRecordDisplay = (records) => {
    var displayRecords = [];

    const actionMap = {
      0: t("workflow-manager.actionErrorLabel", "Posting Failed"),
      1: t("workflow-manager.actionInQueueLabel", "Ready to be Processed"),
      2: t("workflow-manager.actionProcessingLabel", "In Process"),
      3: t("workflow-manager.actionCompletedLabel", "Posted"),
      5: t(
        "workflow-manager.actionCompletedWithWarningLabel",
        "Posted With Warning",
      ),
    };

    const actionLinkMap = {
      0: t("workflow-manager.actionDetailErrorLabel", "Show Details"),
      1: t("workflow-manager.actionDetailInQueueLabel", "Process Record"),
      2: t("workflow-manager.actionDetailProcessingLabel", "Show Details"),
      3: t("workflow-manager.actionDetailCompletedLabel", ""),
      5: t(
        "workflow-manager.actionDetailCompletedWithWarningLabel",
        "Show Details",
      ),
    };

    records.forEach((record) => {
      displayRecords.push({
        id: record.WorkflowRecordId,
        from: record.FromName,
        fromReference: record.SourceReference,
        to: record.ToName,
        toReference: record.TargetReference,
        targetArtifactDictionaryDataId: record.TargetArtifactDictionaryDataId,
        sourceConnectionId: record.SourceConnectionId,
        sourceArtifactDictionaryDataId: record.SourceArtifactDictionaryDataId,
        status: record.Status,
        action: actionMap[record.Status],
        actionLink: actionLinkMap[record.Status],
        fromDate: record.FromDate,
        toDate: record.ToDate,
        isHeld: record.IsHeld,
        isArchived: record.IsArchived,
        createdDate: record.CreatedDate,
        sourceConnectionId: record.SourceConnectionId,
        targetConnectionId: record.TargetConnectionId,
        sourceId: record.SourceArtifactDictionaryDataId,
        targetId: record.TargetArtifactDictionaryDataId,
        isSplit: record.IsSplit,
        isConsolidated: record.IsConsolidated,
        connectionPairId: record.ConnectionPairId,
      });
    });
    return displayRecords;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <WorkflowRecordGrid
        ref={gridRef}
        records={transformRecordDisplay(records)}
        selectionModel={selectionModel}
        setSelectionModel={setSelectionModel}
        operationId={currentOperationId}
        workflowName={workflowName}
        onSearchTrigger={triggerSearchRecords}
        lastAnalyticsRunAt={lastAnalyticsRunAt}
        refreshedDisplayTime={refreshedDisplayTime}
      />
    </Box>
  );
};

const GridHeader = (name) => {
  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          width: "100%",
          backgroundColor: "var(--disabled)",
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          "& > *": {
            paddingBottom: "1rem",
          },
        }}
      >
        <Stack
          direction="row"
          sx={{
            flexGrow: "1",
          }}
        >
          <Box
            sx={{
              paddingRight: "2rem",
            }}
          >
            <WorkflowRecordGridFilter />
          </Box>
          <WorkflowRecordGridSearch searchRecords={name.onSearchTrigger} />
        </Stack>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <HorizontalWorkflowMenu />
        </Box>
      </Stack>
      <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
        <Box
          sx={{
            minheight: "78px",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            padding: "20px 0px 20px 53px",
            borderRadius: "4px",
            border: "1px black",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            style={{
              fontSize: "var(--font-lg)",
              fontWeight: "var(--font-semi-bold)",
            }}
          >
            {name.workflowName}
          </Typography>
          <Typography
            sx={{
              fontSize: "var(--font-sm)",
              opacity: 0.9,
              whiteSpace: "nowrap",
              textAlign: "right",
              paddingRight: "53px",
            }}
          >
            {getRelativeRefreshLabel(
              name.lastAnalyticsRunAt,
              name.refreshedDisplayTime,
            )}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

const WorkflowRecordGridFilter = () => {
  const currentOperationId = useSelector(selectWorkflow);
  const dispatch = useDispatch();

  let status;
  let type;

  const schema = [
    // {
    //   id: 0,
    //   label: "From",
    //   type: FilterDropDownType.CheckboxList,
    //   cascading: true,
    //   childLabel: "Select Source Connections",
    //   setValue: setFilterSourceConnections,
    //   readVal: selectFilterSourceConnections,
    //   labelKey: "connectionName",
    //   uniqueKey: "connectionId",
    //   // value: <CheckboxTest />,
    // },
    // {
    //   id: 1,
    //   label: "To",
    //   type: FilterDropDownType.CheckboxList,
    //   cascading: true,
    //   childLabel: "Select Target Connections",
    //   setValue: setFilterTargetConnections,
    //   readVal: selectFilterTargetConnections,
    //   labelKey: "connectionName",
    //   uniqueKey: "connectionId",
    // },
    {
      id: 0,
      label: "Status",
      type: FilterDropDownType.CheckboxList,
      includingAllCheckBox: true,
      cascading: true,
      childLabel: "Select Status",
      setValue: setFilterStatus,
      readVal: selectFilterStatus,
      labelKey: "label",
      uniqueKey: "id",
    },
    // {
    //   label: "Date",
    //   type: FilterDropDownType.Date,
    //   cascading: true,
    //   id: 3,
    //   childLabel: "Select Date",
    //   setValue: setDateType,
    //   readVal: selectDateType,
    //   setDateValue: setFilterDate,
    //   readDateVal: selectFilterDate,
    //   uniqueKey: "id",
    // },
    {
      id: 1,
      label: "Type",
      type: FilterDropDownType.CheckboxList,
      includingAllCheckBox: true,
      cascading: true,
      childLabel: "Select Type",
      setValue: setFilterType,
      readVal: selectFilterType,
      labelKey: "label",
      uniqueKey: "id",
    },
  ];

  return (
    <>
      <CustomFilterDropdown schemas={schema} />
    </>
  );
};

const WorkflowRecordGridSearch = ({ searchRecords }) => {
  const searchString = useSelector(selectManagerSearchString);
  const { t } = useTranslation("homepage");
  const dispatch = useDispatch();

  const handleTextChange = (e) => {
    const value = e.target.value;
    dispatch(setManagerSearchString({ searchString: value }));
    if (value === "") {
      searchRecords();
    }
  };

  return (
    <>
      <TextField
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon style={{ color: "var(--text-icon-regular)" }} />
            </InputAdornment>
          ),
        }}
        placeholder="Search by Order Details"
        value={searchString || ""} //grab value straight from redux
        onChange={handleTextChange}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            // Prevent's default 'Enter' behavior.
            event.defaultPrevented = true;
            searchRecords();
          }
        }}
        sx={{
          width: "350px",
          "&:hover fieldset": {
            backgroundColor: "#ffffff",
          },
          "&.MuiFormControl-root .MuiInputBase-root": {
            borderRadius: "8px",
          },
          "&:hover fieldset": {
            color: "#000000",
          },
        }}
      />
    </>
  );
};

const memoizedGridHeader = React.memo(
  ({
    workflowName,
    onSearchTrigger,
    lastAnalyticsRunAt,
    refreshedDisplayTime,
    ...props
  }) => {
    return (
      <GridHeader
        {...props}
        workflowName={workflowName}
        onSearchTrigger={onSearchTrigger}
        lastAnalyticsRunAt={lastAnalyticsRunAt}
        refreshedDisplayTime={refreshedDisplayTime}
      />
    );
  },
);

const WorkflowRecordGrid = ({
  records,
  operationId,
  selectionModel,
  setSelectionModel,
  selection,
  workflowName,
  onSearchTrigger,
  lastAnalyticsRunAt,
  refreshedDisplayTime,
}) => {
  const { t } = useTranslation("homepage");
  const gridRef = useGridApiRef();
  const dispatch = useDispatch();
  const currentOperationId = useSelector(selectWorkflow);

  const lifecyclePopupOpen = useSelector(selectLifecyclePopupOpen);
  const selectedLifecycle = useSelector(selectLifecycle);
  const isDataLoaded = useSelector(selectRecordsLoadedStatus);
  const pagination = useSelector(selectSessionPagination);

  const [fromName, setFromName] = useState("");
  const [fromReference, setFromReference] = useState("");
  const isMapOnFlyOpen = useSelector(selectIsMapOnFlyOpen);

  const handleOpenLifecyclePopup = async (params) => {
    setFromName(params.row.from);
    setFromReference(params.row.fromReference);
    try {
      const response = await axios.get("/operationsmanager/lifecycle-popup", {
        params: {
          artifactDictionaryDataId: params.row.sourceArtifactDictionaryDataId,
          lifecycle: selectedLifecycle,
          targetArtifactDictionaryDataId:
            params.row.targetArtifactDictionaryDataId,
        },
      });
      dispatch(setLifecycleDetails({ lifecycleDetails: response.data }));
      dispatch(setLifecyclePopupOpen({ lifecyclePopupOpen: true }));
    } catch (error) {
      dispatch(setLifecyclePopupOpen({ lifecyclePopupOpen: false }));
      alert("Encountered problem in fetching workflows for the lifecycle.");
    }
  };

  const handleOpenWorkflowSteps = async (row) => {
    dispatch(
      setWorkflowStepsInfo({
        workflowRecordId: row.id,
        sourceArtifactDictionaryDataId: row.sourceArtifactDictionaryDataId,
        connectionPairId: row.connectionPairId,
      }),
    );
    dispatch(setSelectedRecord({ row }));

    // heap.io
    window.heap.track("Workflow Steps - Open Screen");
  };

  const handleCloseLifecyclePopup = () => {
    dispatch(setLifecyclePopupOpen({ lifecyclePopupOpen: false }));
  };

  const displayCreatedDateColumn = () => {
    const invoiceToCustomerInvoice = 103010;
    const creditNote = 103020;
    return operationId == invoiceToCustomerInvoice || operationId == creditNote;
  };

  const workflowColumns = React.useMemo(() => [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      minWidth: 53,
    },
    {
      field: "from",
      headerName: t("workflow-manager.fromLabel", "From"),
      flex: 1,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <Stack>
          <Typography style={{ fontWeight: "var(--font-semi-bold" }}>
            {params.row.from ?? "-"}
          </Typography>
          <Typography>{params.row.fromReference ?? "-"}</Typography>
          <Stack
            direction="row"
            sx={{ marginTop: "0.2rem", color: "var(--primary-main)" }}
          >
            {params.row.isSplit ? (
              <>
                <SplitIcon />
                <Typography
                  sx={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}
                >
                  Split
                </Typography>
              </>
            ) : (
              ""
            )}
            {params.row.isHeld ? (
              <>
                <HoldIcon />
                <Typography sx={{ marginLeft: "0.5rem" }}>Hold</Typography>
              </>
            ) : (
              ""
            )}
            {params.row.isArchived ? (
              <>
                <ArchiveIcon />
                <Typography sx={{ marginLeft: "0.5rem" }}>Archive</Typography>
              </>
            ) : (
              ""
            )}
          </Stack>
        </Stack>
      ),
    },
    {
      field: "sourceConnectionId",
    },
    {
      field: "sourceArtifactDictionaryDataId",
    },
    {
      field: "to",
      headerName: t("workflow-manager.toLabel", "To"),
      minWidth: 250,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Stack>
          <Typography style={{ fontWeight: "var(--font-semi-bold)" }}>
            {!params.row.to ? "-" : params.row.to}
          </Typography>
          <Typography>
            {!params.row.toReference ? "-" : params.row.toReference}
          </Typography>
          {params.row.isConsolidated ? (
            <Stack
              direction="row"
              sx={{ marginTop: "0.2rem", color: "var(--primary-main)" }}
            >
              <ConsolidateIcon />
              <Typography sx={{ marginLeft: "0.5rem" }}>
                {t("workflow-manager.consolidate", "Consolidate")}
              </Typography>
            </Stack>
          ) : (
            ""
          )}
        </Stack>
      ),
    },
    {
      field: "targetArtifactDictionaryDataId",
    },
    {
      field: "isHeld",
    },
    {
      field: "isArchived",
    },
    {
      field: "status",
      headerName: t("workflow-manager.statusLabel", "Status"),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <StatusCell row={params.row} onStatusClick={handleOpenWorkflowSteps} />
      ),
    },
    {
      field: "date",
      headerName: t("workflow-manager.dateLabel", "Date"),
      flex: 1.5,
      minWidth: 300,
      sortable: false,
      renderCell: (params) => (
        <Stack>
          <Stack
            direction="row"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Typography
              style={{
                fontWeight: "var(--font-semi-bold)",
                paddingRight: "5px",
              }}
            >
              {t("workflow-manager.receivedDate", "Received:")}
            </Typography>
            <Typography>
              {params.row.fromDate
                ? dayjs(params.row.fromDate)
                    .utc(true)
                    .local()
                    .format("YYYY.MM.DD HH:mm")
                : "-"}
            </Typography>
          </Stack>
          {displayCreatedDateColumn() ? (
            <Stack direction="row">
              <Typography
                style={{
                  fontWeight: "var(--font-semi-bold)",
                  paddingRight: "5px",
                }}
              >
                {t("workflow-manager.createdDateLabel", "Created:")}
              </Typography>
              <Typography>
                {params.row.createdDate
                  ? dayjs(params.row.createdDate)
                      .utc(true)
                      .local()
                      .format("YYYY.MM.DD HH:mm")
                  : "-"}
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
          <Stack direction="row">
            <Typography
              style={{
                fontWeight: "var(--font-semi-bold)",
                paddingRight: "5px",
              }}
            >
              {t("workflow-manager.postedDateLabel", "Posted:")}
            </Typography>
            <Typography>
              {params.row.toDate
                ? dayjs(params.row.toDate)
                    .utc(true)
                    .local()
                    .format("YYYY.MM.DD HH:mm")
                : "-"}
            </Typography>
          </Stack>
        </Stack>
      ),
    },
    {
      field: "lifecycle",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <ShepTooltip title="Lifecycle">
          <IconButton
            onClick={(event) => {
              event.stopPropagation();
              handleOpenLifecyclePopup(params);

              // heap.io
              window.heap.track("Lifecycle - Open Screen");
            }}
            className={"workflow-row"}
            style={{ cursor: "pointer" }}
          >
            <LifecycleIcon style={{ width: 22, height: 24 }} />
          </IconButton>
        </ShepTooltip>
      ),
    },
    {
      field: "threedotMenu",
      type: "actions",
      sortable: false,
      getActions: (params) => [
        <ThreeDotSettingMenu
          getSelectedOption={(option, taskType, action, rowData) => {}}
          taskType={0}
          rowData={params.row}
        />,
      ],
    },
  ]);

  const handleMapOnFlyPopup = () => {
    dispatch(setIsMapOnFlyOpen({ value: false }));
  };

  const handlePaginationModelChange = async (newPaginationModel) => {
    //send newPaginationModel to reducer in workflowManagerSessionSlice
    dispatch(setSessionPagination({ newPaginationModel }));
  };

  return (
    <>
      <ShepDataGridPro
        isDataLoaded={isDataLoaded}
        secondsToStall={20}
        apiRef={gridRef}
        rows={records}
        columns={workflowColumns}
        columnHeaderHeight={78}
        rowHeight={100}
        pagination
        initialState={{
          pagination: {
            paginationModel: { pageSize: 15, page: 0 },
          },
        }}
        onPaginationModelChange={handlePaginationModelChange}
        slots={{
          baseCheckbox: ShepCheckbox,
          pagination: ShepPagination,
          toolbar: memoizedGridHeader,
        }}
        slotProps={{
          toolbar: {
            workflowName,
            onSearchTrigger,
            lastAnalyticsRunAt,
            refreshedDisplayTime,
          },
        }}
        paginationModel={pagination}
        whiteHeader={true}
        checkboxSelection
        disableMultipleRowSelection={true}
        disableColumnMenu={true}
        disableColumnFilter={true}
        disableColumnReorder={true}
        hideFooterSelectedRowCount={true}
        columnVisibilityModel={{
          targetArtifactDictionaryDataId: false,
          sourceArtifactDictionaryDataId: false,
          sourceConnectionId: false,
          isHeld: false,
          isArchived: false,
        }}
        sx={{
          color: "var(--text-primary)",
          fontSize: "var(--font-sm)",
          fontFamily: "var(--font-family)",
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          backgroundColor: "var(--default-light)",
          border: "none",
          "& .MuiDataGrid:hover": {
            backgroundColor: "inherit", // Or 'transparent' or whatever color you'd like
          },
          "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
            opacity: 0,
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "var(--tertiary-13)",
          },
          "& .workflow-row:hover": {
            backgroundColor: "transparent",
          },
          "& .Mui-selected": {
            backgroundColor: "var(--primary-pale)",
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
            {
              outline: "none",
            },
          "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
            {
              opacity: 1,
            },
          "& .MuiDataGrid-cellCheckbox": {
            borderLeft: "8px solid transparent",
          },
          "& .Mui-selected .MuiDataGrid-cellCheckbox": {
            opacity: 1,
            borderLeft: "8px solid var(--primary-main)",
            borderTopLeftRadius: "4px",
            borderBottomLeftRadius: "4px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontSize: "var(--font-md)",
            fontWeight: "var(--font-bold)",
            borderLeft: "none",
          },
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            backgroundColor: "var(--primary-pale)",
            borderBottom: "none",
            borderLeft: "none",
          },
          "& .MuiCheckbox-root": {
            display: "block",
            paddingBottom: "10px",
          },
        }}
      />

      <Dialog
        className="massive map-on-fly"
        open={isMapOnFlyOpen}
        onClose={handleMapOnFlyPopup}
        maxWidth="xl"
        fullWidth={true}
        PaperProps={{
          className: "dialogPaper",
          sx: {
            zIndex: 1305,
            position: "absolute",
            left: "50%",
            transform: "translate(-50%,0)",
          },
        }}
        slotProps={{
          backdrop: { sx: { zIndex: 1300, position: "absolute" } },
        }}
      >
        <DialogTitle disableTypography className="header">
          <Typography variant="subtitle">Map On Fly</Typography>
          <IconButton className="closeIconButton" onClick={handleMapOnFlyPopup}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className="dialogContent">
          <MapOnFly />
        </DialogContent>
      </Dialog>
      <LifeCyclePopup
        open={lifecyclePopupOpen}
        onClose={handleCloseLifecyclePopup}
        fromName={fromName}
        fromReference={fromReference}
      />
    </>
  );
};

export default WorkflowManagerPage;
